import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (stored && token) {
      setUser(JSON.parse(stored));
      api.get('/auth/me').then(res => {
        setUser(res.data.user);
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';
  const isAdminOrBM = ['admin', 'business_manager'].includes(user?.role);
  const isAdminOrTL = ['admin', 'team_lead'].includes(user?.role);
  const isBDE = user?.role === 'bde';
  const isViewer = user?.role === 'viewer';
  // isReadOnly: hide every mutate-UI element when true. Currently
  // implies viewer, but kept as a separate name so callers don't
  // hard-code the role string.
  const isReadOnly = isViewer;
  // Viewer-inclusive variants of the role gates, used by the sidebar
  // and route guards so co-founders can reach the dashboards and
  // strategy pages without expanding the strict admin/TL/BM gates.
  const canSeeTeamData = ['admin', 'team_lead', 'viewer'].includes(user?.role);
  const canSeeStrategy = ['admin', 'business_manager', 'viewer'].includes(user?.role);
  // Upwork Referrals access — admin/team_lead always have it; any other
  // role (bde, viewer) gets in via the users.can_access_upwork_referrals
  // flag, granted by admin from User Management. Mirrors the backend
  // requireUpworkAccess middleware so the UI and API agree.
  const canAccessUpwork = isAdminOrTL || !!user?.can_access_upwork_referrals;

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading,
      isAdmin, isAdminOrBM, isAdminOrTL, isBDE, isViewer, isReadOnly,
      canSeeTeamData, canSeeStrategy, canAccessUpwork,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
